import type { TButtonMenuOption } from "components/shared/ButtonOptionsMenu";
import StyledChip from "components/shared/StyledChip";
import type { AGREEMENTS_ROLES } from "containers/ScopeContext";
import { AGREEMENTS_ROLES_ALL } from "containers/ScopeContext";
import { SmallBody } from "helpers/TypographyComponents";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { NotificationRoleOption, NotificationRolePermissions } from "store/notificationStore";
import { setNotificationRolePermission, updateNotificationRoles } from "store/notificationStore";
import type { RoleListItem } from "store/permissionsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import AddRolesOptions from "../AddRolesOptions";
import styles from "../style.module.scss";
import type { NotificationType } from "../utils";
import { formatNotificationRolesPayload } from "../utils";

interface IRenderRoleCells {
    /**
     * Notification role permissions
     */
    permissions: NotificationRolePermissions;
    /**
     * All roles from /company/roles (system + custom)
     */
    allRoles: RoleListItem[];
    /**
     * Notification roles options computed from allRoles
     */
    notificationRolesOptions: NotificationRoleOption | undefined;
}
/**
 * Render role cells
 * @param permissions - notification role permissions
 * @param allRoles - all roles from /company/roles
 * @param notificationRolesOptions - notification roles options
 * @returns - render role cells
 */
const RenderRoleCells = ({ permissions, allRoles, notificationRolesOptions }: IRenderRoleCells) => {
    const { getCompanySettings } = useCompanySettings();
    const { notificationRolesPermissions } = useAppSelector((state) => state.notification);
    const { notificationType } = permissions;
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const usesAgreements = getCompanySettings("uses_agreements");

    let rolesOptions = notificationRolesOptions?.[notificationType];

    // Filter agreements roles if the company does not use agreements
    const filteredAllRoles = usesAgreements
        ? allRoles
        : allRoles.filter((role) => !AGREEMENTS_ROLES_ALL.includes(role.name as AGREEMENTS_ROLES));

    if (!usesAgreements) {
        rolesOptions = rolesOptions?.filter((opt) => !AGREEMENTS_ROLES_ALL.includes(opt.id as AGREEMENTS_ROLES));
    }
    const initialChosenOptions = rolesOptions?.filter((opt) => opt.value === 1);

    const [chosenOptions, setChosenOptions] = useState<TButtonMenuOption[] | undefined>(initialChosenOptions);
    const [isDirty, setIsDirty] = useState(false);

    const handleDeleteRole = (notificationType: NotificationType, role: string) => {
        if (!chosenOptions) return;
        const _chosenOptions = chosenOptions.filter((option) => option.id !== role);
        handleUpdateRole(_chosenOptions, notificationType);
        setChosenOptions(_chosenOptions);
        dispatch(setNotificationRolePermission({ chosenOptions: _chosenOptions, notificationType }));
    };

    /**
     * Handle update role
     * @param chosenOptions - chosen options
     * @param notificationType - notification type
     */
    const handleUpdateRole = async (chosenOptions: TButtonMenuOption[], notificationType: NotificationType) => {
        if (!notificationRolesPermissions) return;
        const data = formatNotificationRolesPayload(
            notificationRolesPermissions,
            chosenOptions,
            notificationType,
            filteredAllRoles,
        );
        await dispatch(updateNotificationRoles({ data }));
        setIsDirty(false);
    };

    if (!rolesOptions) return;

    // Use chosenOptions to display chips (reflects current selection including custom roles)
    const displayedRoles = chosenOptions ?? [];

    return (
        <div className={styles["notified-roles-cell"]}>
            {!displayedRoles.length && (
                <SmallBody color="grey" thin italic>
                    {t("ScopedNotificationSettings.NoRoles")}
                </SmallBody>
            )}
            {displayedRoles.map((option) => (
                <StyledChip
                    key={option.id}
                    label={option.label}
                    onDelete={() => handleDeleteRole(notificationType, option.id as string)}
                />
            ))}
            <AddRolesOptions
                rolesOptions={rolesOptions}
                handleUpdateRole={(chosenOptions) => isDirty && handleUpdateRole(chosenOptions, notificationType)}
                setChosenOptions={setChosenOptions}
                chosenOptions={chosenOptions}
                setIsDirty={setIsDirty}
            />
        </div>
    );
};

export default RenderRoleCells;
